import { useEffect, useMemo, useState } from 'react';
import { type IMoodRecordDraftComplete } from '@shared/types';
import { validateMoodRecordDraftComplete } from '@shared/utils/validateMoodRecord';
import { getDebugRing } from '@lib/devUtils';
import { getMoodUI } from '@lib/moodUtils/getMoodUIDetails';
import { clx } from '@lib/utils';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal/Modal';
import { EditPencil } from '../../components/icons/EditPencil';
import { SaveDisk } from '../../components/icons/SaveDisk';
import { Trashcan } from '../../components/icons/Trashcan';
import { useMoodRecordDraft } from '../../lib/moodUtils/MoodRecordDraftContext';
import { Heading } from './Heading';
import { PhotoComponent } from './PhotoComponent';

interface ICompleteAndCompletedPageContentProps {
  children: React.ReactNode;
  handleDelete?: () => void;
  handleEdit?: () => void;
  handleSaveClick?: () => Promise<void>;
  isCompleted: boolean;
}

type ModalAction = 'edit' | 'delete';

export const CompleteAndCompletedPageContent = ({
  children,
  handleDelete,
  handleEdit,
  handleSaveClick,
  isCompleted = false,
}: ICompleteAndCompletedPageContentProps) => {
  const [modalAction, setModalAction] = useState<ModalAction | undefined>(undefined);
  const [completeMoodRecord, setCompleteMoodRecord] = useState<IMoodRecordDraftComplete>();
  const { moodRecordDraft, photoFile } = useMoodRecordDraft();

  const photoUrl = useMemo(() => {
    return photoFile ? URL.createObjectURL(photoFile) : undefined;
  }, [photoFile]);

  useEffect(() => {
    if (!moodRecordDraft) return;
    try {
      validateMoodRecordDraftComplete(moodRecordDraft);
      setCompleteMoodRecord(moodRecordDraft);
    } catch (error) {
      setCompleteMoodRecord(undefined);
      console.error('Error validating mood record draft.', error);
    }
  }, [moodRecordDraft]);

  const moodUiDetails = completeMoodRecord ? getMoodUI(completeMoodRecord.mood) : undefined;

  // Fallback to display a placeholder if the complete mood record is not found.
  // @TODO: either use a redirect or have some UI to navigate away from here.
  // @TODO: also, these flash when the page loads because the completeMoodRecord is undefined.
  if (!completeMoodRecord || !moodUiDetails) {
    return <div>No complete mood record to display</div>;
  }

  const { mood, moodDate, emotions, triggers, note } = completeMoodRecord;
  const { faceEmoji } = moodUiDetails;

  return (
    <>
      {/* Mood and date */}
      <Heading className={clx('mx-auto my-3 w-10/12 text-center', getDebugRing('yellow'))}>
        <div className="flex items-center justify-center pb-2">{faceEmoji}</div>
        <span className="text-lg text-black">
          On {moodDate}, you were feeling: <strong>{mood}</strong>
        </span>
      </Heading>

      {/* Emotions */}
      <div className={clx('flex gap-2.5', getDebugRing('purple'))}>
        {emotions.map((emotion) => (
          <div className="bg-background-tertiary rounded-full border px-2.5 py-1.5" key={emotion}>
            {emotion}
          </div>
        ))}
      </div>

      {/* Triggers */}
      <div className={clx('flex gap-2.5', getDebugRing('orange'))}>
        {triggers.map((trigger) => (
          <div className="bg-background-secondary rounded-full border px-2.5 py-1.5" key={trigger}>
            {trigger}
          </div>
        ))}
      </div>

      {/* Photo */}
      {photoUrl && (
        <div className={clx('mt-auto flex gap-2.5')}>
          <PhotoComponent photoUrl={photoUrl} />
        </div>
      )}

      {/* Notes */}
      {note && (
        <div
          className={clx(
            'relative flex w-full gap-2.5',
            getDebugRing('green'),
            photoUrl ? '' : 'mt-auto'
          )}
        >
          <textarea
            className="bg-background-tertiary w-full resize-none rounded-2xl border border-black p-2"
            rows={3}
            name="note"
            maxLength={100}
            value={note}
            readOnly
          />
          <div className="absolute bottom-2 right-2 text-xs opacity-50">
            {note.length}/{100}
          </div>
        </div>
      )}

      {/* Edit and Save buttons */}
      <div className={clx('mx-1 mb-10 flex', getDebugRing('yellow'), 'mr-auto')}>
        {isCompleted && (
          <Button as="button" onClick={() => setModalAction('edit')} variant="minimalSize">
            <EditPencil />
          </Button>
        )}
        {handleSaveClick && (
          <Button as="button" onClick={handleSaveClick} variant="minimalSize">
            <SaveDisk />
          </Button>
        )}
        <Button as="button" onClick={() => setModalAction('delete')} variant="minimalSize">
          <Trashcan />
        </Button>
      </div>

      {/* Action modal */}
      <Modal
        isOpen={modalAction !== undefined}
        containerClassName={clx(
          'bg-background-quaternary w-11/12 rounded-2xl border-none text-sm drop-shadow',
          'mt-auto max-w-xs -translate-y-full pb-1 pt-4 text-left',
          'animate-slide-in-up-fast'
        )}
      >
        You are about to {modalAction} this mood log entry.
        <div className="text-foreground-secondary mt-2 flex gap-6 p-1 font-medium">
          {handleEdit && (
            <Button variant="minimalSizeIcon" onClick={handleEdit}>
              Edit
            </Button>
          )}
          {handleDelete && (
            <Button variant="minimalSizeIcon" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button variant="minimalSizeIcon" onClick={() => setModalAction(undefined)}>
            Cancel
          </Button>
        </div>
      </Modal>

      {children}
    </>
  );
};
